import axios from 'axios';
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@eriko/shared-types';
import { useAuthStore } from '../stores/authStore';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// ---------------------------------------------------------------------------
// Token refresh queue — prevents concurrent refresh calls
// ---------------------------------------------------------------------------

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}> = [];

function processRefreshQueue(error: Error | null, token: string | null): void {
  for (const { resolve, reject } of refreshQueue) {
    if (error) reject(error);
    else resolve(token!);
  }
  refreshQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, updateTokens, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    throw new Error('No refresh token');
  }

  try {
    const res = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
      `${BASE_URL}/auth/refresh`,
      { refreshToken },
    );

    const body = res.data;
    if (!body.success) {
      clearAuth();
      window.location.href = '/login';
      throw new Error('Token refresh failed');
    }

    updateTokens(body.data);
    return body.data.accessToken;
  } catch {
    clearAuth();
    window.location.href = '/login';
    throw new Error('Token refresh failed');
  }
}

// ---------------------------------------------------------------------------
// Shared envelope-unwrapping response interceptor
// ---------------------------------------------------------------------------

function addEnvelopeInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<unknown>>) => {
      const body = response.data;
      if (body && typeof body === 'object' && 'success' in body) {
        if (body.success) {
          // Replace response.data with the inner data so callers get T directly
          (response as AxiosResponse<unknown>).data = body.data;
          return response;
        }
        // API returned success: false — surface as an error
        const err = new Error(body.error?.message || 'API Error') as Error & {
          code?: string;
          status?: number;
        };
        err.code = body.error?.code;
        err.status = response.status;
        return Promise.reject(err);
      }
      return response;
    },
    (error) => {
      // Network / non-2xx error without an envelope
      const body = error.response?.data as ApiResponse<unknown> | undefined;
      if (body && typeof body === 'object' && 'success' in body && !body.success) {
        const apiErr = new Error(body.error?.message || 'API Error') as Error & {
          code?: string;
          status?: number;
        };
        apiErr.code = body.error?.code;
        apiErr.status = error.response?.status;
        return Promise.reject(apiErr);
      }
      return Promise.reject(error);
    },
  );
}

// ---------------------------------------------------------------------------
// apiClient — authenticated instance
// ---------------------------------------------------------------------------

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: inject Bearer token
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor: handle 401 + token refresh with queue
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success) {
        (response as AxiosResponse<unknown>).data = body.data;
        return response;
      }
      const err = new Error(body.error?.message || 'API Error') as Error & {
        code?: string;
        status?: number;
      };
      err.code = body.error?.code;
      err.status = response.status;
      return Promise.reject(err);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status: number | undefined = error.response?.status;

    // 402 TRIAL_EXPIRED → take user to /billing. Still throw so callers can react.
    if (status === 402) {
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/billing')) {
        window.location.href = '/billing';
      }
      const apiErr = new Error('TRIAL_EXPIRED') as Error & { code?: string; status?: number };
      apiErr.code = 'TRIAL_EXPIRED';
      apiErr.status = 402;
      return Promise.reject(apiErr);
    }

    // Unwrap API envelope errors from non-401 failures
    if (status !== 401) {
      const body = error.response?.data as ApiResponse<unknown> | undefined;
      if (body && typeof body === 'object' && 'success' in body && !body.success) {
        const apiErr = new Error(body.error?.message || 'API Error') as Error & {
          code?: string;
          status?: number;
        };
        apiErr.code = body.error?.code;
        apiErr.status = status;
        return Promise.reject(apiErr);
      }
      return Promise.reject(error);
    }

    // 401 handling — attempt token refresh
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue request until refresh completes
      return new Promise<AxiosResponse>((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            originalRequest._retry = true;
            resolve(apiClient(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processRefreshQueue(null, newToken);
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return apiClient(originalRequest);
    } catch (refreshErr) {
      processRefreshQueue(refreshErr as Error, null);
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);

// ---------------------------------------------------------------------------
// publicClient — unauthenticated instance
// ---------------------------------------------------------------------------

export const publicClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

addEnvelopeInterceptor(publicClient);
